import { Alert, Button, Grid, Typography, useTheme } from '@mui/material';
import React, { useState } from 'react';
import PropertyForm from '../../components/property/PropertyForm';
import { useMutation } from '@apollo/client';
import {
  getProperties,
  createProperty as createPropertyMutation,
  Property,
  MIXPANEL_EVENTS,
} from 'vendor-marketplace-shared-library';
import useAlert from '../../hooks/useAlert';
import { PropertyFormInput } from '../../types/property';
import useAuth from '../../hooks/useAuth';
import useMixpanel from '../../hooks/useMixpanel';

export default function CreatePropertyPage() {
  const alert = useAlert();
  const { userQuery, logout, setForcePropertyPageOnboarding } = useAuth();
  const theme = useTheme();
  const { mixpanelTrack } = useMixpanel();
  const [loading, setLoading] = useState(false);
  const [previouslyAddedProperty, setPreviouslyAddedProperty] = useState<Property | null>(null);
  const [formData, setFormData] = useState<PropertyFormInput>({
    name: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipcode: '',
  });

  const [createProperty] = useMutation(createPropertyMutation, {
    refetchQueries: [getProperties],
    onError: () => {
      alert.setAlert({ severity: 'error', message: 'PROPERTY_ADD_ERROR' });
    },
    onCompleted: async data => {
      setLoading(false);
      setPreviouslyAddedProperty(data.createProperty);
      setFormData({
        name: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipcode: '',
      });
    },
  });

  const onCreate = async () => {
    setLoading(true);
    setForcePropertyPageOnboarding(true);
    await createProperty({
      variables: {
        input: {
          name: formData.name || null,
          address1: formData.address1 || null,
          address2: formData.address2 || null,
          city: formData.city || null,
          state: formData.state || null,
          zipcode: formData.zipcode || null,
        },
      },
    });
  };

  const refetchUserQuery = async () => {
    setForcePropertyPageOnboarding(false);
    await userQuery.refetch();
  };

  return (
    <Grid container sx={styles.mainContainer}>
      {/*Card*/}
      <Grid item sx={[styles.card, { border: `1px ${theme.palette.secondary.light} solid` }]}>
        {/*Card Header*/}
        <Grid container item xs={12} sx={styles.headerContainer}>
          <Grid item>
            <img height={75} width={200} src={'procure-logo-black.png'} alt={'logo'} />
          </Grid>
          <Grid item>
            <Typography sx={{ textAlign: 'center' }} variant={'h6'}>
              Add a property to your organization
            </Typography>
          </Grid>
        </Grid>

        {/*New Property Created Alert*/}
        {previouslyAddedProperty && (
          <Grid item xs={12} sx={styles.pb}>
            <Alert severity='success'>{`You have successfully created ${previouslyAddedProperty.name} property.`}</Alert>
          </Grid>
        )}

        {/*Property Form*/}
        {!loading && (
          <Grid container item xs={12} sx={styles.pb}>
            <PropertyForm
              onSubmit={() => {
                onCreate();
                mixpanelTrack(MIXPANEL_EVENTS.ONBOARDING_ADD_PROPERTY_PRESSED);
              }}
              loading={loading}
              formData={formData}
              setFormData={setFormData}
              submitButtonText={previouslyAddedProperty ? 'Add another Property' : 'Add Property'}
            />
          </Grid>
        )}

        {/*Skip or Logout Button*/}
        <Grid item xs={12}>
          <Button fullWidth onClick={previouslyAddedProperty ? refetchUserQuery : logout}>
            {previouslyAddedProperty ? 'skip' : 'logout'}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
}

const styles = {
  mainContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
  },
  card: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'white',
    maxWidth: '600px',
    minWidth: '400px',
    padding: 4,
  },
  headerContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    paddingBottom: 4,
  },
  pb: {
    paddingBottom: 2,
  },
};
